import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaEnvelope, FaArrowRight, FaDownload, FaChevronDown } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProjectCard from '../components/ProjectCard';
import SectionHeader from '../components/SectionHeader';
import projectsData from '../data/projects.json';
import '../styles/Home.css';

const Home: React.FC = () => {
  const featuredProjects = projectsData.filter((project) => project.featured).slice(0, 3);
  const [scrollIndicatorOpacity, setScrollIndicatorOpacity] = useState(1);

  useEffect(() => {
    // Load Elfsight platform script for the LinkedIn activities feed
    if (document.querySelector('script[src="https://elfsightcdn.com/platform.js"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    // Handle scroll indicator opacity based on scroll position
    const handleScroll = () => {
      const heroSection = document.querySelector('.hero-section') as HTMLElement;
      if (!heroSection) return;

      const heroHeight = heroSection.offsetHeight;
      const scrolled = window.scrollY;
      
      // Start fading when user scrolls, fully transparent at 75% of hero height
      const fadeStartPoint = 0;
      const fadeEndPoint = heroHeight * 0.75;
      
      let opacity = 1;
      if (scrolled > fadeStartPoint) {
        opacity = Math.max(0, 1 - (scrolled - fadeStartPoint) / (fadeEndPoint - fadeStartPoint));
      }
      
      setScrollIndicatorOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const footer = document.querySelector('.footer');
    footer?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToProjects = () => {
    const projectsSection = document.querySelector('.featured-projects-section');
    projectsSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home">
      <Navbar />

      {/* Hero/Intro Section */}
      <section className="hero-section">
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="hero-text">
              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                Hi, I'm <span className="text-accent">Dawson Pent</span>
              </motion.h1>
              <motion.h2
                className="hero-subtitle"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                Software Engineer: Full-Stack, AI, and Embedded Systems
              </motion.h2>
              <motion.p
                className="hero-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                I'm a passionate developer specializing in building exceptional experiences.
                I love creating innovative AI architectures, embedded systems, integrated
                full-stack applications, and user-focused productivity tools that make a difference.
              </motion.p>

              <motion.div
                className="hero-actions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <button onClick={scrollToContact} className="btn-primary">
                  Contact Me
                </button>
                <Link to="/resume" className="btn-secondary">
                  <FaDownload /> Resume
                </Link>
              </motion.div>

              <motion.div
                className="hero-socials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <a
                  href="https://github.com/dawsonp2003"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  aria-label="GitHub"
                >
                  <FaGithub />
                </a>
                <a
                  href="https://www.linkedin.com/in/dawson-pent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin />
                </a>
                <a
                  href="mailto:dawson1203@gmail.com"
                  className="social-icon"
                  aria-label="Email"
                >
                  <FaEnvelope />
                </a>
              </motion.div>
            </div>

             <motion.div
              className="hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              <div className="image-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/me.jpg`}
                  alt="Dawson Pent"
                  onError={(e) => {
                    e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/images/placeholder-profile.png`;
                    e.currentTarget.onerror = null;
                  }}
                />
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="scroll-indicator"
            initial={{ opacity: 0, y: -20 }}
            animate={{ 
              opacity: scrollIndicatorOpacity, 
              y: scrollIndicatorOpacity > 0.1 ? [0, 10, 0] : 0 
            }}
            transition={{ 
              delay: 0.8, 
              duration: 0.6, 
              repeat: scrollIndicatorOpacity > 0.1 ? Infinity : 0, 
              repeatType: 'reverse', 
              repeatDelay: 0.3 
            }}
            onClick={scrollToProjects}
            style={{ pointerEvents: scrollIndicatorOpacity < 0.1 ? 'none' : 'auto' }}
          >
            <FaChevronDown />
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="featured-projects-section">
        <div className="container">
          <SectionHeader
            title="Featured Projects"
            subtitle="Some of my recent work that I'm proud of"
          />
          <motion.div
            className="projects-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, staggerChildren: 0.2 }}
          >
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <ProjectCard {...project} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="view-all-projects"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <Link to="/projects" className="btn-primary">
              See All Projects <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <SectionHeader
            title="About Me"
            subtitle="A brief introduction to who I am and what I do"
          />
          <motion.div
            className="about-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              I'm a dedicated software developer with a passion for creating efficient, scalable,
              and user-friendly applications.  My experience spans embedded systems and IoT devices,
              full-stack web applications, robotics, controls systems, and AI including enterprise LLM and
              RAG solutions.
            </p>
            <p>
              I've supported enterprise AI adoption building and testing RAG agents with Copilot Studio and
              Azure, monitored LLM performance with Power BI, and engineered Databricks-based voice analytics
              pipelines.
            </p>
            <p>
              In my research at school, I've implemented embedded ML (LiteRT) for real-time sound
              classification on custom hardware.  With experience in C-family languages, Java,
              JavaScript/TypeScript, Python, and more - I enjoy tackling complex problems and turning ideas
              into reality while staying current with new tools and best practices.
            </p>
            <p>
              When I'm not coding, you'll often find me deep in strategy video games, fantasy books, or a good
              movie.  I also explore new technologies through personal projects, give back through volunteering,
              and recharge outdoors through backpacking, camping, rafting, and more.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Activities Feed Section */}
      <section className="activities-section">
        <div className="container">
          <SectionHeader
            title="Recent Activities"
            subtitle="Stay updated with my latest work and achievements via LinkedIn"
            centered
          />
          <motion.div
            className="activities-feed-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="elfsight-app-f6ab4a08-6d1f-477c-9564-1142e8dc9df2" data-elfsight-app-lazy></div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;

